import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import ProfilePicture from './ProfilePicture';
import { Image, Users, Star, Calendar, Filter, Search, Eye } from 'lucide-react';

const GalleryBrowser = ({ galleryType, username, characterId }) => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mystveil_token'));
  const [activeTab, setActiveTab] = useState(galleryType || 'recent');
  const [searchTerm, setSearchTerm] = useState('');

  const globalGalleryTypes = [
    { value: 'recent', label: 'Recent', endpoint: '/api/mystveil/galleries/recent' },
    { value: 'featured', label: 'Featured', endpoint: '/api/mystveil/galleries/featured' },
    { value: 'official', label: 'Official', endpoint: '/api/mystveil/galleries/official' }
  ];

  const userGalleryTypes = [
    { value: 'recent', label: 'Recent', endpoint: '/api/mystveil/galleries/recent' },
    { value: 'featured', label: 'Featured', endpoint: '/api/mystveil/galleries/featured' }
  ];

  useEffect(() => {
    const loadGalleries = async () => {
      if (!token) {
        setError('Please log in to view galleries');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        let endpoint = '';
        if (username) {
          endpoint = `https://api.4fingerstudios.com/api/mystveil/galleries/player/${username}`;
        } else if (characterId) {
          endpoint = `https://api.4fingerstudios.com/api/mystveil/galleries/character/${characterId}`;
        } else {
          const galleryType = globalGalleryTypes.find(t => t.value === activeTab);
          endpoint = `https://api.4fingerstudios.com/api/mystveil/galleries/${activeTab}`;
        }

        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please log in again.');
            localStorage.removeItem('mystveil_token');
            setToken(null);
          } else {
            throw new Error(`Failed to load galleries: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        setGalleries(data || []);
      } catch (err) {
        setError('Failed to load galleries');
        console.error('Galleries load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadGalleries();
  }, [token, activeTab, username, characterId]);

  const filteredGalleries = galleries.filter(gallery => 
    !searchTerm || 
    gallery.imageTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gallery.imageDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gallery.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Gallery Browser</h1>
            <p className="mystical-text mb-6">Please log in to view galleries</p>
            <Link to="/auth/login" className="mystical-button">
              Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card">
            <p className="mystical-text">Loading galleries...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Error</h1>
            <p className="mystical-text mb-6 text-red-600">{error}</p>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mystical-card mb-8">
            <h1 className="mystical-title text-black mb-6">
              {username ? `${username}'s Art Gallery` : 
               characterId ? `Character Gallery` : 
               'Global Art Gallery'}
            </h1>
            
            {/* Gallery Type Tabs */}
            {!username && !characterId && (
              <div className="flex flex-wrap gap-2 mb-6">
                {globalGalleryTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setActiveTab(type.value)}
                    className={`mystical-button ${
                      activeTab === type.value 
                        ? 'bg-purple-800 text-white' 
                        : 'bg-white bg-opacity-95 text-black hover:bg-purple-100'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            )}
            
            {/* User Gallery Type Tabs */}
            {username && (
              <div className="flex flex-wrap gap-2 mb-6">
                {userGalleryTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setActiveTab(type.value)}
                    className={`mystical-button ${
                      activeTab === type.value 
                        ? 'bg-purple-800 text-white' 
                        : 'bg-white bg-opacity-95 text-black hover:bg-purple-100'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            )}

            {/* Search Bar */}
            <div className="mystical-card bg-white bg-opacity-95 mb-6">
              <div className="flex items-center space-x-3">
                <Search className="text-gray-500" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  placeholder="Search galleries by title, description, or username..."
                />
                <Filter className="text-gray-500" size={20} />
              </div>
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="mystical-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="mystical-subtitle text-black text-xl">
                {filteredGalleries.length} {filteredGalleries.length === 1 ? 'Artwork' : 'Artworks'}
              </h3>
              <div className="flex items-center space-x-2">
                <Eye className="text-gray-500" size={20} />
                <span className="mystical-text text-gray-600">
                  {filteredGalleries.length} items
                </span>
              </div>
            </div>

            {filteredGalleries.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredGalleries.map(gallery => (
                  <div key={gallery.id} className="mystical-card bg-white bg-opacity-95">
                    <div className="aspect-square mb-4 overflow-hidden rounded-md">
                      <img 
                        src={gallery.imageUrl} 
                        alt={gallery.imageTitle}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <h4 className="mystical-subtitle text-black text-lg line-clamp-2">
                        {gallery.imageTitle || 'Untitled'}
                      </h4>
                      
                      <p className="mystical-text text-gray-600 text-sm line-clamp-2">
                        {gallery.imageDescription || 'No description provided'}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <Users className="text-gray-500" size={16} />
                          <span className="mystical-text text-gray-600">
                            {gallery.username}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="text-gray-500" size={16} />
                          <span className="mystical-text text-gray-600">
                            {new Date(gallery.submittedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="mystical-text text-sm text-gray-500">
                            {gallery.artType}
                          </span>
                          {gallery.characterCount > 1 && (
                            <span className="mystical-text text-sm text-purple-800 font-bold">
                              {gallery.characterCount} chars
                            </span>
                          )}
                        </div>
                        
                        {gallery.isFeatured && (
                          <div className="flex items-center space-x-1">
                            <Star className="text-yellow-500" size={16} />
                            <span className="mystical-text text-sm text-yellow-600 font-bold">
                              Featured
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="mystical-text text-sm text-gray-500">
                            Status:
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            gallery.status === 'approved' ? 'bg-green-100 text-green-800' :
                            gallery.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {gallery.status}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <span className="mystical-text text-sm text-purple-800 font-bold">
                            {gallery.totalChips} chips
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mystical-card bg-white bg-opacity-95 text-center">
                <Image className="mx-auto text-gray-400 mb-4" size={64} />
                <h3 className="mystical-subtitle text-black text-xl mb-4">
                  {searchTerm ? 'No Results Found' : 'No Artworks Yet'}
                </h3>
                <p className="mystical-text text-gray-600 mb-6">
                  {searchTerm 
                    ? 'Try adjusting your search terms or browse different galleries.'
                    : 'Be the first to submit artwork to this gallery!'
                  }
                </p>
                {!searchTerm && (
                  <Link to="/submit" className="mystical-button">
                    Submit Your First Artwork
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Gallery Stats */}
          {filteredGalleries.length > 0 && (
            <div className="mystical-card mt-8">
              <h3 className="mystical-subtitle text-black text-xl mb-6">
                Gallery Statistics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="mystical-card bg-white bg-opacity-95 text-center">
                  <h4 className="mystical-subtitle text-black mb-2">Total Artworks</h4>
                  <p className="mystical-text text-3xl font-bold text-purple-800">
                    {filteredGalleries.length}
                  </p>
                </div>
                <div className="mystical-card bg-white bg-opacity-95 text-center">
                  <h4 className="mystical-subtitle text-black mb-2">Featured Artworks</h4>
                  <p className="mystical-text text-3xl font-bold text-yellow-600">
                    {filteredGalleries.filter(g => g.isFeatured).length}
                  </p>
                </div>
                <div className="mystical-card bg-white bg-opacity-95 text-center">
                  <h4 className="mystical-subtitle text-black mb-2">Total Chips Awarded</h4>
                  <p className="mystical-text text-3xl font-bold text-green-600">
                    {filteredGalleries.reduce((sum, g) => sum + (g.totalChips || 0), 0)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/profile" className="mystical-button">
              My Profile
            </Link>
            <Link to="/submit" className="mystical-button">
              Submit Artwork
            </Link>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default GalleryBrowser;
